import { trackDocMatchesSong } from './studentTracksModel';

/**
 * Removing a song from a student's repertoire (Anthony, 2026-09-27: "I want to
 * be able to remove any repertoire that's been added as well, if necessary").
 *
 * HIDDEN, NEVER ERASED (his choice of the two offered): the repertoire row,
 * that song's Song Notes and its track pointers are all stamped with
 * `removedAt` / `removedBy`, and the notes and pointers also with
 * `removedWith` = the repertoire row's id, so a restore puts back exactly what
 * that removal took and nothing else. Every read path filters on `removedAt`
 * in the service layer (RepertoireService, SongNotesService,
 * StudentTracksService), so the lesson board, the profile, Recital Planning and
 * the family Songs page all agree. Admin / owner only, enforced in the rules:
 * a teacher can neither set nor clear these fields.
 *
 * WHY NOTES AND POINTERS ARE STAMPED TOO: a family never reads `repertoire`
 * (its Songs page is built from delivered notes + track pointers), and the
 * staff surfaces show a pointer no song claims as "Tracks on file" and a note
 * no song claims under "Warm-ups & other notes". Hiding only the row would
 * move its content somewhere else instead of hiding it.
 */
export const REMOVAL_FIELDS = ['removedAt', 'removedBy', 'removedWith'];

export const isRemoved = (doc) => Boolean(doc && doc.removedAt);

export const withoutRemoved = (rows = []) => (Array.isArray(rows) ? rows : []).filter((r) => r && !isRemoved(r));

export const onlyRemoved = (rows = []) => (Array.isArray(rows) ? rows : []).filter(isRemoved);

/**
 * The track pointers a removal should hide: the ones that match the removed
 * song (the same predicate the strip uses) AND no other song still on the
 * list. A pointer matched by name can belong to two rows of one title (a song
 * re-added after removal); hiding it would take the other row's tracks too.
 * Already-hidden pointers are left alone so a restore of the OTHER removal
 * still owns them.
 */
export const pointersToHide = (trackDocs = [], entry, visibleEntries = []) => {
	if (!entry) return [];
	const others = (Array.isArray(visibleEntries) ? visibleEntries : []).filter((e) => e && e.id !== entry.id && !isRemoved(e));
	return (Array.isArray(trackDocs) ? trackDocs : []).filter(
		(doc) => doc && !isRemoved(doc) && trackDocMatchesSong(doc, entry) && !others.some((e) => trackDocMatchesSong(doc, e))
	);
};
